import { ID } from 'shared/types/id';
import {
  TimelineDropSlot,
  TimelineDropSlotKinds,
  TimelineDropTargetKinds,
} from 'shared/types/timeline';

import { rowGapBandRatio, rowHeight } from './constants';
import { SideBarItem } from './types';

export type ResolveSidebarDropSlotOptions = {
  rows: SideBarItem[];
  rowTops: number[];
  cursorX: number;
  cursorY: number;
  draggedGoalId: ID;
  blockedIds: Set<ID>;
  indentBase: number;
  indentWidth: number;
};

export const isSameTimelineDropSlot = (
  a: TimelineDropSlot | null,
  b: TimelineDropSlot | null,
): boolean => {
  if (!a || !b) {
    return a === b;
  }
  return (
    a.kind === b.kind &&
    a.targetKind === b.targetKind &&
    a.targetId === b.targetId &&
    a.insertIndex === b.insertIndex &&
    a.depth === b.depth &&
    a.top === b.top
  );
};

const resolveLineSlot = (
  boundary: number,
  {
    rows,
    rowTops,
    cursorX,
    draggedGoalId,
    blockedIds,
    indentBase,
    indentWidth,
  }: ResolveSidebarDropSlotOptions,
): TimelineDropSlot | null => {
  // the position above a leading header means "first in that category"
  const b = boundary === 0 && 'lifeArea' in rows[0] ? 1 : boundary;
  const above = b > 0 ? rows[b - 1] : undefined;
  const below = b < rows.length ? rows[b] : undefined;
  const top = b === 0 ? rowTops[0] : rowTops[b - 1] + rowHeight;

  if (!above) {
    const categoryId = rows[0].categoryId;
    return {
      kind: TimelineDropSlotKinds.Line,
      targetKind: TimelineDropTargetKinds.Category,
      targetId: categoryId,
      categoryId,
      insertIndex: 0,
      top,
      depth: 0,
    };
  }

  if ('lifeArea' in above) {
    return {
      kind: TimelineDropSlotKinds.Line,
      targetKind: TimelineDropTargetKinds.Category,
      targetId: above.id,
      categoryId: above.id,
      insertIndex: 0,
      top,
      depth: 0,
    };
  }

  // an expanded parent directly above its first child leaves exactly one
  // interpretation for the gap: first child of that parent
  const belowIsFirstChild =
    !!below &&
    'goal' in below &&
    below.categoryId === above.categoryId &&
    below.level === above.level + 1;
  if (belowIsFirstChild) {
    if (blockedIds.has(above.id)) {
      return null;
    }
    return {
      kind: TimelineDropSlotKinds.Line,
      targetKind: TimelineDropTargetKinds.Goal,
      targetId: above.id,
      categoryId: above.categoryId,
      insertIndex: 0,
      top,
      depth: above.level + 1,
    };
  }

  const maxDepth = above.level;
  const minDepth =
    below && 'goal' in below && below.categoryId === above.categoryId
      ? below.level
      : 0;
  const preferredDepth = Math.floor((cursorX - indentBase) / indentWidth);
  const depth = Math.min(Math.max(preferredDepth, minDepth), maxDepth);

  let insertIndex = 0;
  let parentGoalId: ID | null = null;
  const preceding = rows.slice(0, b).reverse();
  for (const row of preceding) {
    if ('lifeArea' in row || row.categoryId !== above.categoryId) {
      break;
    }
    if (row.level < depth) {
      parentGoalId = row.level === depth - 1 ? row.id : null;
      break;
    }
    if (row.level === depth && row.id !== draggedGoalId) {
      insertIndex += 1;
    }
  }

  if (parentGoalId && blockedIds.has(parentGoalId)) {
    return null;
  }

  return {
    kind: TimelineDropSlotKinds.Line,
    targetKind: parentGoalId
      ? TimelineDropTargetKinds.Goal
      : TimelineDropTargetKinds.Category,
    targetId: parentGoalId ?? above.categoryId,
    categoryId: above.categoryId,
    insertIndex,
    top,
    depth,
  };
};

export const resolveSidebarDropSlot = (
  options: ResolveSidebarDropSlotOptions,
): TimelineDropSlot | null => {
  const { rows, rowTops, cursorY, blockedIds } = options;
  if (!rows.length) {
    return null;
  }

  const bandIndex = rowTops.findIndex(
    (top) => cursorY >= top && cursorY < top + rowHeight,
  );

  if (bandIndex === -1) {
    const nextRowIndex = rowTops.findIndex((top) => cursorY < top);
    return resolveLineSlot(
      nextRowIndex === -1 ? rows.length : nextRowIndex,
      options,
    );
  }

  const row = rows[bandIndex];
  if ('lifeArea' in row) {
    return {
      kind: TimelineDropSlotKinds.Nest,
      targetKind: TimelineDropTargetKinds.Category,
      targetId: row.id,
      categoryId: row.id,
      insertIndex: 0,
      top: rowTops[bandIndex],
      depth: 0,
    };
  }

  const relativeY = cursorY - rowTops[bandIndex];
  const inNestBand =
    relativeY >= rowHeight * rowGapBandRatio &&
    relativeY <= rowHeight * (1 - rowGapBandRatio);
  if (inNestBand) {
    if (blockedIds.has(row.id)) {
      return null;
    }
    return {
      kind: TimelineDropSlotKinds.Nest,
      targetKind: TimelineDropTargetKinds.Goal,
      targetId: row.id,
      categoryId: row.categoryId,
      insertIndex: 0,
      top: rowTops[bandIndex],
      depth: row.level,
    };
  }

  const boundary =
    relativeY < rowHeight * rowGapBandRatio ? bandIndex : bandIndex + 1;
  return resolveLineSlot(boundary, options);
};
